import dayjs from 'dayjs';
import { loadImageBase64 } from '../utils/loadImageBase64';
import { buildUploadUrl } from '../config/runtimeConfig';

// กำหนด font สำหรับ pdfMake
const initializePdfMake = () => {
    if (window.pdfMake) {
        window.pdfMake.fonts = {
            THSarabunNew: {
                normal: 'THSarabunNew.ttf',
                bold: 'THSarabunNew Bold.ttf',
                italics: 'THSarabunNew Italic.ttf',
                bolditalics: 'THSarabunNew BoldItalic.ttf'
            }
        };
    }
};

// Helper function to get status text in Thai
const getReceiptStatusText = (status) => {
    const statusMap = {
        'invoice': 'ใบแจ้งหนี้',
        'paid': 'ชำระแล้ว',
        'partial': 'ชำระบางส่วน',
        'overdue': 'เกินกำหนด',
        'cancelled': 'ยกเลิก'
    };
    return statusMap[status] || status;
};

/**
 * สร้าง PDF สำหรับตารางรายการใบเสร็จ
 * @param {Array} receipts - รายการใบเสร็จทั้งหมดที่จะพิมพ์
 * @param {Object} company - ข้อมูลบริษัท
 * @param {Object} filters - ตัวกรองที่ใช้ (สำหรับแสดงในหัวเอกสาร)
 * @param {Object} stats - สถิติรวม
 * @returns {Promise<string>} - Data URL ของ PDF
 */
export const generateReceiptsTablePDF = async (receipts, company = {}, filters = {}, stats = {}) => {
    // โหลด logo
    let logoBase64 = null;
    if (company?.logo_path) {
        try {
            logoBase64 = await loadImageBase64(buildUploadUrl(company.logo_path));
        } catch (error) {
            console.warn('ไม่สามารถโหลด logo ได้:', error);
        }
    }

    // ตรวจสอบว่า pdfMake โหลดแล้วหรือยัง
    if (!window.pdfMake) {
        throw new Error('pdfMake library is not loaded');
    }

    // กำหนด font
    initializePdfMake();

    // สร้างข้อความแสดงช่วงเวลาที่กรอง
    let filterText = 'รายการทั้งหมด';
    if (filters.start_date && filters.end_date) {
        filterText = `ช่วงเวลา: ${dayjs(filters.start_date).format('DD/MM/YYYY')} - ${dayjs(filters.end_date).format('DD/MM/YYYY')}`;
    } else if (filters.start_date) {
        filterText = `ตั้งแต่: ${dayjs(filters.start_date).format('DD/MM/YYYY')}`;
    } else if (filters.end_date) {
        filterText = `ถึง: ${dayjs(filters.end_date).format('DD/MM/YYYY')}`;
    }

    // เพิ่มข้อมูลตัวกรองอื่นๆ
    const filterDetails = [];
    if (filters.search) filterDetails.push(`ค้นหา: "${filters.search}"`);
    if (filters.status) filterDetails.push(`สถานะ: ${getReceiptStatusText(filters.status)}`);
    if (filters.contact_id) filterDetails.push(`กรองตามลูกค้า`);

    // สร้างตาราง
    const tableBody = [
        // Header row
        [
            { text: 'เลขที่เอกสาร', style: 'tableHeader', alignment: 'center' },
            { text: 'วันที่', style: 'tableHeader', alignment: 'center' },
            { text: 'ลูกค้า', style: 'tableHeader' },
            { text: 'ยอดรวม', style: 'tableHeader', alignment: 'right' },
            { text: 'ชำระแล้ว', style: 'tableHeader', alignment: 'right' },
            { text: 'คงเหลือ', style: 'tableHeader', alignment: 'right' },
            { text: 'สถานะ', style: 'tableHeader', alignment: 'center' }
        ],
        // Data rows
        ...receipts.map(receipt => {
            const totalAmount = parseFloat(receipt.total_amount || 0);
            const paidAmount = parseFloat(receipt.paid_amount || 0);
            const remaining = totalAmount - paidAmount;

            return [
                { text: receipt.invoice_number || receipt.receipt_number || '-', fontSize: 9 },
                { text: dayjs(receipt.receipt_date).format('DD/MM/YYYY'), fontSize: 9, alignment: 'center' },
                { text: receipt.contact_name || '-', fontSize: 9 },
                { text: totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 }), fontSize: 9, alignment: 'right' },
                { text: paidAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 }), fontSize: 9, alignment: 'right', color: paidAmount > 0 ? '#52c41a' : '#000000' },
                { text: remaining.toLocaleString('th-TH', { minimumFractionDigits: 2 }), fontSize: 9, alignment: 'right', color: remaining > 0 ? '#cf1322' : '#52c41a' },
                { text: getReceiptStatusText(receipt.status), fontSize: 9, alignment: 'center' }
            ];
        })
    ];

    // เพิ่มแถวสรุปยอดรวม
    if (receipts.length > 0) {
        tableBody.push([
            { text: 'รวมทั้งสิ้น', bold: true, fontSize: 10, colSpan: 3, alignment: 'right' },
            {},
            {},
            {
                text: (stats.total_sales || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                bold: true,
                fontSize: 10,
                alignment: 'right',
                fillColor: '#f0f0f0'
            },
            {
                text: Number(stats.collected_on_documents || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                bold: true,
                fontSize: 10,
                alignment: 'right',
                fillColor: '#f0f0f0',
                color: '#52c41a'
            },
            {
                text: (stats.outstanding_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                bold: true,
                fontSize: 10,
                alignment: 'right',
                fillColor: '#f0f0f0',
                color: '#cf1322'
            },
            { text: `${stats.total_receipts || 0} รายการ`, bold: true, fontSize: 10, alignment: 'center', fillColor: '#f0f0f0' }
        ]);
    }

    const docDefinition = {
        pageSize: 'A4',
        pageOrientation: 'landscape', // แนวนอนเพื่อให้ตารางกว้างขึ้น
        pageMargins: [40, 80, 40, 60],
        defaultStyle: {
            font: 'THSarabunNew',
            fontSize: 14
        },
        header: function (currentPage, pageCount) {
            return {
                columns: [
                    {
                        width: 100,
                        ...(logoBase64 ? {
                            image: logoBase64,
                            width: 60,
                            height: 60,
                            margin: [40, 10, 0, 0]
                        } : {
                            text: '',
                            margin: [0, 0, 0, 0]
                        })
                    },
                    {
                        width: '*',
                        stack: [
                            { text: company.company_name || 'ชื่อบริษัท', fontSize: 16, bold: true, alignment: 'center', margin: [0, 15, 0, 2] },
                            { text: company.address || '', fontSize: 10, alignment: 'center', margin: [0, 0, 0, 2] },
                            { text: `โทร: ${company.phone || '-'} | เลขประจำตัวผู้เสียภาษี: ${company.tax_id || '-'}`, fontSize: 10, alignment: 'center' }
                        ]
                    },
                    {
                        width: 100,
                        text: ''
                    }
                ],
                margin: [0, 0, 0, 10]
            };
        },
        footer: function (currentPage, pageCount) {
            return {
                text: `หน้า ${currentPage} จาก ${pageCount} | พิมพ์เมื่อ: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
                alignment: 'center',
                fontSize: 9,
                margin: [0, 10, 0, 0]
            };
        },
        styles: {
            header: {
                fontSize: 18,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            subheader: {
                fontSize: 12,
                margin: [0, 5, 0, 10]
            },
            tableHeader: {
                bold: true,
                fontSize: 10,
                color: 'white',
                fillColor: '#1890ff',
                alignment: 'center'
            },
            summaryBox: {
                fillColor: '#f0f0f0',
                margin: [0, 10, 0, 10]
            }
        },
        content: [
            {text: 'ยอดตามเอกสารรวม VAT · รวมค้างชำระ/รออนุมัติ · ยอดสรุปไม่นับเอกสารยกเลิก',fontSize:12,margin:[0,0,0,8]},
            {text: `ก่อน VAT ${Number(stats.revenue_before_vat || 0).toLocaleString('th-TH',{minimumFractionDigits:2,maximumFractionDigits:2})} บาท · ภาษีขาย ${Number(stats.output_vat || 0).toLocaleString('th-TH',{minimumFractionDigits:2,maximumFractionDigits:2})} บาท`,fontSize:12,margin:[0,0,0,8]},
            // หัวข้อรายงาน
            {
                text: 'รายงานใบเสร็จ/ใบแจ้งหนี้',
                style: 'header'
            },

            // ข้อมูลการกรอง
            {
                columns: [
                    {
                        width: '*',
                        text: [
                            { text: filterText, fontSize: 11, bold: true },
                            filterDetails.length > 0 ? '\n' + filterDetails.join(' | ') : ''
                        ]
                    },
                    {
                        width: 'auto',
                        text: `วันที่พิมพ์: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
                        fontSize: 10,
                        alignment: 'right'
                    }
                ],
                margin: [0, 0, 0, 15]
            },

            // สถิติสรุป
            {
                columns: [
                    {
                        width: '25%',
                        stack: [
                            { text: 'ยอดขายรวม', fontSize: 10, color: '#666' },
                            { text: `${(stats.total_sales || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท`, fontSize: 14, bold: true, color: '#3f8600' }
                        ]
                    },
                    {
                        width: '25%',
                        stack: [
                            { text: 'ใบเสร็จทั้งหมด', fontSize: 10, color: '#666' },
                            { text: `${stats.total_receipts || 0} รายการ`, fontSize: 14, bold: true, color: '#1890ff' }
                        ]
                    },
                    {
                        width: '25%',
                        stack: [
                            { text: 'ชำระแล้ว', fontSize: 10, color: '#666' },
                            { text: `${stats.paid_receipts || 0} รายการ`, fontSize: 14, bold: true, color: '#52c41a' }
                        ]
                    },
                    {
                        width: '25%',
                        stack: [
                            { text: 'ค้างชำระ', fontSize: 10, color: '#666' },
                            { text: `${(stats.outstanding_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท`, fontSize: 14, bold: true, color: '#cf1322' }
                        ]
                    }
                ],
                margin: [0, 0, 0, 15]
            },

            // ตารางรายการ
            {
                table: {
                    headerRows: 1,
                    widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto'],
                    body: tableBody
                },
                layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                        if (rowIndex === 0) return '#1890ff';
                        if (rowIndex === node.table.body.length - 1) return '#f0f0f0';
                        return (rowIndex % 2 === 0) ? '#f9f9f9' : null;
                    },
                    hLineWidth: function (i, node) {
                        return (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.5;
                    },
                    vLineWidth: function (i) {
                        return 0.5;
                    },
                    hLineColor: function (i) {
                        return '#ddd';
                    },
                    vLineColor: function (i) {
                        return '#ddd';
                    }
                }
            }
        ]
    };

    try {
        const pdfDocGenerator = window.pdfMake.createPdf(docDefinition);

        return new Promise((resolve, reject) => {
            pdfDocGenerator.getDataUrl((dataUrl) => {
                if (dataUrl) resolve(dataUrl);
                else reject(new Error('ไม่สามารถสร้าง PDF ได้'));
            });
        });
    } catch (error) {
        console.error('Error generating receipts table PDF:', error);
        throw new Error('ไม่สามารถสร้าง PDF ได้: ' + error.message);
    }
};

/**
 * ดาวน์โหลด PDF ตารางรายการใบเสร็จ
 */
export const downloadReceiptsTablePDF = async (receipts, company = {}, filters = {}, stats = {}) => {
    const dataUrl = await generateReceiptsTablePDF(receipts, company, filters, stats);

    // สร้างชื่อไฟล์
    const dateStr = dayjs().format('YYYYMMDD_HHmm');
    const fileName = `รายงานใบเสร็จ_${dateStr}.pdf`;

    // ดาวน์โหลด
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    link.click();

    return { success: true, fileName };
};
